import { createApi } from "@reduxjs/toolkit/query/react";

import { baseQuery } from "../../../shared/api/base-query";
import { pagedQueryFn } from "../../../shared/api/paged-query-fn";
import { PagedResponseDto } from "../../../shared/dtos/paged-response.dto";
import { buildFilterCondition, buildFilterExpression } from "../../../shared/utils";
import {
  ITechOperationParamWithArrayParamsDto,
  ITechOperationSubGroupParamWithArrayParamsDto,
} from "../shared/dtos/tech-operation-param.dto";
import { ITechOperationDto } from "../shared/dtos/tech-operation.dto";

export type FetchAreaHistory_forOpNumberRequest = {
  farmLandId: string;
  realFarmLandId?: string;
  techOperationGroupId: string;
  operationNumber: number;
};

export type FetchOperationNumbers_forGroupRequest = {
  farmLandId: string;
  techOperationGroupId: string;
};

export const techopsRelatedApiSlice = createApi({
  reducerPath: "api-techop-related",
  baseQuery,
  tagTypes: ["OperationNumbersForGroup"],
  endpoints: (builder) => ({
    fetchAreaHistory_forOpNo: builder.query<
      ITechOperationDto[],
      FetchAreaHistory_forOpNumberRequest
    >({
      queryFn: pagedQueryFn<
        ITechOperationDto,
        ITechOperationDto[],
        FetchAreaHistory_forOpNumberRequest
      >(
        ({ farmLandId, realFarmLandId, techOperationGroupId, operationNumber }) => ({
          url: "TechOperationsV2",
          params: {
            filter: buildFilterExpression(
              buildFilterCondition("farmLandId", "==", farmLandId),
              buildFilterCondition("techOperationGroupId", "==", techOperationGroupId),
              realFarmLandId &&
                buildFilterCondition("RealFarmLandId", "==", realFarmLandId),
              operationNumber > 0 &&
                buildFilterCondition("operationNumber", "==", operationNumber)
            ),
          },
          method: "GET",
        }),
        (dtos) => dtos
      ),
    }),

    fetchOpNumbers_forGroup: builder.query<
      Record<number, ITechOperationDto>,
      FetchOperationNumbers_forGroupRequest
    >({
      queryFn: pagedQueryFn<
        ITechOperationDto,
        Record<number, ITechOperationDto>,
        FetchOperationNumbers_forGroupRequest
      >(
        ({ farmLandId, techOperationGroupId }) => ({
          url: "TechOperationsV2",
          params: {
            filter: buildFilterExpression(
              buildFilterCondition("farmLandId", "==", farmLandId),
              buildFilterCondition("techOperationGroupId", "==", techOperationGroupId)
            ),
            sort: "operationNumber desc",
          },
          method: "GET",
        }),
        (dtos) =>
          Object.fromEntries(dtos.map((techOp) => [techOp.operationNumber || 0, techOp]))
      ),
      providesTags: ["OperationNumbersForGroup"],
    }),

    fetchSubGroupParams_forSubGroup: builder.query<
      ITechOperationParamWithArrayParamsDto[],
      string
    >({
      query: (subGroupId: string) => {
        return {
          url: `TechOperationSubGroupParamsV2/${subGroupId}`,
          method: "GET",
        };
      },
      transformResponse: (
        response: PagedResponseDto<ITechOperationSubGroupParamWithArrayParamsDto>
      ): ITechOperationParamWithArrayParamsDto[] => {
        return response.data.map((item): ITechOperationParamWithArrayParamsDto => {
          const param = item.techOperationParam;
          return {
            ...param,
            techOperationArrayParams: param.techOperationArrayParams ?? [],
          };
        });
      },
    }),
  }),
});

export const {
  useFetchAreaHistory_forOpNoQuery,
  useLazyFetchAreaHistory_forOpNoQuery,
  useLazyFetchOpNumbers_forGroupQuery,
  useLazyFetchSubGroupParams_forSubGroupQuery,
  useFetchOpNumbers_forGroupQuery,
} = techopsRelatedApiSlice;

export const invalidateOperationNumbersForGroup = () => {
  return techopsRelatedApiSlice.util.invalidateTags(["OperationNumbersForGroup"]);
};
